import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

// Import i18n for translations
import "./i18n"; // Assurez-vous que votre fichier de configuration i18n est correct.
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

import HomePageVitrine from "./pages/Home/HomePageVitrine";
import EspaceCoach from "./pages/EspaceCoach/EspaceCoach.jsx";
import JudoExpert from "./pages/JudoExpert/JudoExpert.jsx";
import LoginPage from "./pages/LoginPage/LoginPage";
import RegisterPage from "./pages/RegisterPage/RegisterPage";

import ResetPwd from "./pages/ResetPwd/ResetPwd";
import ForgotPasswordPage from "./pages/ResetPwd/ForgotPasswordPage";
import { UserProvider } from "./pages/Home/UserContext";
import PrimaryColorProvider from "./hooks/PrimaryColorProvider.jsx";
import { NotificationToastProvider } from "./components/Notification/NotificationManager.jsx";
import { SidebarProvider } from "./contexts/SidebarContext.js";

import Navbar4 from "./pages/Home/Navbar4/Navbar4";
import Footer from "./pages/Home/Footer";

import { MessageProvider } from "./hooks/MessageContext.js";
import { ContactFormProvider } from "./hooks/ContactFormContext.js";
import { GroupProvider } from "./hooks/GroupContext.js";

import ErrorPage from "./pages/ErrorPage/ErrorPage";

// Documents
import ReglementInterieur from "./pages/Documents/ReglementInterieur";
import ConditionsUtilisationPage from "./pages/rgpd/ConditionsUtilisationPage.jsx";
import PolitiqueConfidentialitePage from "./pages/rgpd/PolitiqueConfidentialitePage.jsx";
import FaqPage from "./pages/rgpd/FaqPage.jsx";
import MentionsLegalesPage from "./pages/rgpd/MentionsLegalesPage.jsx";

// PagesJudokas
import ResultatsPage from "./pages/PagesJudokas/Resultats/ResultatsPage";
import PassageGrade from "./pages/PagesJudokas/PassageGrade/PassageGrade";
import PalmaresPage from "./pages/PagesJudokas/PalmaresPage/PalmaresPage";
import PageJudoka from "./pages/PagesJudokas/PageJudoka/PageJudoka";
import LexiquePage from "./pages/PagesJudokas/LexiquePage/LexiquePage.jsx";
import Profile from "./pages/Profile/Profile";
import StatistiquesJudokaPage from "./pages/PagesJudokas/StatistiquesJudokaPage/StatistiquesJudokaPage";
import SharedEventsPage from "./pages/PagesJudokas/SharedEventsTable/SharedEventsPage.jsx";
import TechniquesRevisionPage from "./pages/PagesJudokas/TechniquesView/TechniquesRevisionPage.jsx";
import TechniquesUV from "./pages/PagesJudokas/TechniquesView/TechniquesUV.jsx";
import TechniquesKata from "./pages/PagesJudokas/TechniquesView/TechniquesKata.jsx";
import TechniquesQuizz from "./pages/PagesJudokas/TechniquesView/TechniquesQuizz.jsx";
import ArbitrageJudo from "./pages/PagesJudokas/Arbitrage/ArbitrageJudo.jsx";
import ReglementCS from "./pages/PagesJudokas/Arbitrage/ReglementCS.jsx";
import ArbitrageQuizz from "./pages/PagesJudokas/Arbitrage/ArbitrageQuizz.jsx";
import PricingPage from "./pages/PagesJudokas/PricingPage/PricingPage.jsx";
import MessagerieJudoka from "./pages/PagesJudokas/MessagerieJudoka/MessagerieJudoka.jsx";
import EventsJudokaPage from "./pages/PagesJudokas/EventPage/EventsJudokaPage.jsx";
import JoinGroup from "./pages/PagesJudokas/JoinGroup/JoinGroup.jsx";
import JudoScorePage from "./pages/PagesJudokas/JudoScorePage/JudoScorePage.jsx";
import PreparationPhysiqueAdherent from "./pages/PagesJudokas/PreparationPhysiqueAdherent/PreparationPhysiqueAdherent.jsx";

// PagesCoach
import GroupsPage from "./pages/PagesCoach/GroupsPage/GroupsPage";
import GroupResultsPage from "./pages/PagesCoach/GroupResultsPage/GroupResultsPage.jsx";
import JoinGroupPage from "./pages/PagesCoach/JoinGroupPage/JoinGroupPage.jsx";
import GroupRequestsPage from "./pages/PagesCoach/GroupRequestsTable/GroupRequestsTable.jsx";
import StatistiquesGroupesJudokaPage from "./pages/PagesCoach/StatistiquesGroupesJudokaPage/StatistiquesGroupesJudokaPage";
import PassageGradeCoach from "./pages/PagesCoach/PassageGradeCoach/PassageGradeCoach.jsx";
import AttendancePage from "./pages/PagesCoach/Attendance/AttendanceDashboard.jsx";
import PreparationPhysiqueCoach from "./pages/PagesCoach/PreparationPhysiqueCoach/PreparationPhysiqueCoach.jsx";

// Pages Administrateurs
import AsideMenu from "./components/AsideMenu/AsideMenu"; // Menu latéral
import MainContent from "./components/MainContent/MainContent"; // Contenu principal

// PagesUtilisateurs
import ChatPage from "./pages/PagesUtilisateurs/ChatPage/ChatPage";

// PagesAdministrateurs
import SettingsPage from "./pages/PagesAdministrateurs/SettingsPage/SettingsPage.jsx";

// ... importez d'autres pages si nécessaire
import "./App.css";
import "./bulma.min.css";

const stripePromise = loadStripe(
  "pk_test_51POgHVEr14Icaey6JeVMxJf9bAJS9AxDAGUTSDI80b01LVDorrxErNHC1HbTU3WRgJfPD3P40daULyEzeakVDBfk00fHPS7sVp",
);

const App = () => {
  // Gestion des states
  const [userInfo, setUserInfo] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(true);

  useEffect(() => {
    const getLocalStorageItem = (key) => {
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.error(
          "Erreur lors de la lecture du localStorage pour la clé",
          key,
          ":",
          error,
        );
        return null;
      }
    };

    const storedUserInfo = getLocalStorageItem("userInfo");
    if (storedUserInfo) {
      setUserInfo(storedUserInfo);
    } else {
      console.error(
        "Les informations de l'utilisateur stockées sont introuvables ou corrompues.",
      );
      // Actions supplémentaires ici, comme une redirection ou une récupération depuis un serveur
    }
  }, []);

  const RequireAuth = ({ children, roles }) => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo")); // Assure-toi que userInfo est stocké dans localStorage
    const location = useLocation();

    if (!userInfo) {
      return <div>Chargement...</div>; // Ou un composant de chargement plus sophistiqué
    }

    if (roles && !roles.includes(userInfo.role_id)) {
      // Rôle non autorisé donc redirection vers une page d'erreur ou d'accueil
      return <Navigate to="/unauthorized" replace />;
    }

    return children;
  };

  // Pour l'exemple, nous allons simuler un utilisateur connecté
  const user = {
    id: 1,
    nom: "Doe",
    prenom: "John",
    email: "JohnDoe@gmail.com",
    photoURL: "path_to_photo.jpg", // Remplace par le chemin réel de la photo
    role_id: 1, // 1 = Administrateur
    userId: 1,
  };

  return (
    <UserProvider>
      <Router>
        <Elements stripe={stripePromise}>
          <I18nextProvider i18n={i18n}>
            <MessageProvider>
              <ContactFormProvider>
                <GroupProvider>
                  <PrimaryColorProvider>
                    <NotificationToastProvider>
                      <SidebarProvider>
                        <div className="flex flex-col min-h-screen bg-base-200 dark:bg-gray-600 dark:text-gray-200">
                          <Navbar4
                            user={{
                              nom: userInfo?.nom ?? "Chargement...",
                              prenom: userInfo?.prenom ?? "",
                              role_id: userInfo?.role_id ?? 0,
                              photoURL: userInfo?.photoURL ?? "",
                              userId: userInfo?.id ?? 0,
                            }}
                          />
                          <Routes>
                            {/* Route pour la page d'accueil du site vitrine */}
                            <Route path="/" element={<HomePageVitrine />} />

                            {/* Route pour la page JudoScore Pro */}
                            <Route
                              path="/judo-score"
                              element={<JudoScorePage />}
                            />

                            {/* Route pour la page de Rejoindre un groupe */}
                            <Route
                              path="/groups/:encryptedId"
                              element={
                                <RequireAuth>
                                  <JoinGroupPage />
                                </RequireAuth>
                              }
                            />

                            <Route
                              path="/coach/requests"
                              element={
                                <RequireAuth>
                                  <GroupRequestsPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Pricing */}
                            <Route
                              path="/formules-tarifs"
                              element={<PricingPage />}
                            />

                            {/* Routes administratives protégées */}
                            <Route
                              path="/appli/*"
                              element={
                                <RequireAuth>
                                  <div className="flex">
                                    <AsideMenu />
                                    <MainContent />
                                  </div>
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Résultats */}
                            <Route
                              path="/resultats"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <ResultatsPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Passage de Grade */}
                            <Route
                              path="/passage-grade"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <PassageGrade />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Palmarès */}
                            <Route
                              path="/palmares"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <PalmaresPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Profil Utilisateur */}
                            <Route
                              path="/profile-utilisateur"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <Profile />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Profil Judoka */}
                            <Route
                              path="/profile-judoka"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <PageJudoka />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Statistiques Judoka */}
                            <Route
                              path="/statistiques-judoka"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <StatistiquesJudokaPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Statistiques Groupes Judoka */}
                            <Route
                              path="/statistiques-groupes-judoka"
                              element={
                                <RequireAuth roles={[1, 5, 6, 7]}>
                                  <StatistiquesGroupesJudokaPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page des événements */}
                            <Route
                              path="/events-judoka"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <EventsJudokaPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page des événements partagés */}
                            <Route
                              path="/shared-events"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <SharedEventsPage />
                                </RequireAuth>
                              }
                            />

                            <Route
                              path="/techniques-judo"
                              element={
                                <RequireAuth roles={[1, 3, 5, 6, 7]}>
                                  <TechniquesRevisionPage />
                                </RequireAuth>
                              }
                            />

                            <Route
                              path="/techniques-uv"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <TechniquesUV />
                                </RequireAuth>
                              }
                            />

                            <Route
                              path="/techniques-kata"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <TechniquesKata />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page Quizz Techniques */}
                            <Route
                              path="/techniques-quizz"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <TechniquesQuizz />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page arbitrage */}
                            <Route
                              path="/arbitrage-judo"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <ArbitrageJudo />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page Règlement CS */}
                            <Route
                              path="/reglement-cs"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <ReglementCS />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page Quizz Arbitrage */}
                            <Route
                              path="/arbitrage-quizz"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <ArbitrageQuizz />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Préparation Physique Adhérent */}
                            <Route
                              path="/preparation-physique-adherent"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <PreparationPhysiqueAdherent />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page des groupes */}
                            <Route
                              path="/groups-coach"
                              element={
                                <RequireAuth roles={[1, 5, 6, 7]}>
                                  <GroupsPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page des Groupes */}
                            <Route
                              path="/group-results"
                              element={
                                <RequireAuth roles={[1, 5, 6, 7]}>
                                  <GroupResultsPage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Passage de Grade Coach */}
                            <Route
                              path="/passage-grade-coach"
                              element={
                                <RequireAuth roles={[1, 5, 6, 7]}>
                                  <PassageGradeCoach />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de l'appel */}
                            <Route
                              path="/attendance"
                              element={
                                <RequireAuth roles={[1, 5, 6, 7]}>
                                  <AttendancePage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Préparation Physique Coach */}
                            <Route
                              path="/preparation-physique-coach"
                              element={
                                <RequireAuth roles={[1, 5, 6, 7]}>
                                  <PreparationPhysiqueCoach />
                                </RequireAuth>
                              }
                            />

                            {/* Messagerie Judoka */}
                            <Route
                              path="/messagerie-judoka"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <MessagerieJudoka />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour la page de Lexique */}
                            <Route
                              path="/lexique"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <LexiquePage />
                                </RequireAuth>
                              }
                            />

                            {/* Route pour rejoindre un groupe */}
                            <Route
                              path="/join-group"
                              element={
                                <RequireAuth roles={[1, 2, 3, 5, 6, 7]}>
                                  <JoinGroup />
                                </RequireAuth>
                              }
                            />

                            {/* /////////////////////////////////////////////////////////// */}

                            <Route
                              path="/chat"
                              element={
                                <RequireAuth roles={[2, 3, 1]}>
                                  <ChatPage />
                                </RequireAuth>
                              }
                            />

                            <Route
                              path="/parametres"
                              element={
                                <RequireAuth roles={[1]}>
                                  <SettingsPage />
                                </RequireAuth>
                              }
                            />

                            {/* /////////////////////////////////////////////////////////// */}

                            <Route
                              path="/espace-coach"
                              element={<EspaceCoach />}
                            />

                            <Route
                              path="/judo-expert"
                              element={<JudoExpert />}
                            />

                            <Route path="/login" element={<LoginPage />} />
                            <Route
                              path="/register"
                              element={<RegisterPage />}
                            />
                            <Route
                              path="/reset-password/:token"
                              element={<ResetPwd />}
                            />
                            <Route
                              path="/forgot-password"
                              element={<ForgotPasswordPage />}
                            />
                            <Route path="/404" element={<ErrorPage />} />
                            <Route path="*" element={<ErrorPage />} />
                            <Route
                              path="/reglement-interieur"
                              element={<ReglementInterieur />}
                            />
                            <Route
                              path="/conditions-utilisation"
                              element={<ConditionsUtilisationPage />}
                            />
                            <Route
                              path="/politique-confidentialite"
                              element={<PolitiqueConfidentialitePage />}
                            />
                            <Route path="/faq" element={<FaqPage />} />
                            <Route
                              path="/mentions-legales"
                              element={<MentionsLegalesPage />}
                            />
                          </Routes>
                          <Footer />
                        </div>
                      </SidebarProvider>
                    </NotificationToastProvider>
                  </PrimaryColorProvider>
                </GroupProvider>
              </ContactFormProvider>
            </MessageProvider>
          </I18nextProvider>
        </Elements>
      </Router>
    </UserProvider>
  );
};

export default App;
